import { useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { translations } from './translations';

type Consent = {
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

const STORAGE_KEY = 'cookieConsent';

export default function CookieBanner() {
  const location = useLocation();
  const { lang } = useLanguage();
  const t = translations[lang].cookie;
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState<Consent>({
    functional: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const alreadyConsented = !!localStorage.getItem(STORAGE_KEY);
    if (alreadyConsented) return;

    // Everywhere except the homepage, show right away — there's no intro
    // animation competing for the same corner of the screen.
    if (location.pathname !== '/') {
      setVisible(true);
      return;
    }

    // On the homepage, wait until the intro is actually done (either it
    // already played earlier this session, or it just finished/was
    // skipped) so the banner never fights the intro for screen space.
    if (sessionStorage.getItem('introPlayed') === 'true') {
      setVisible(true);
      return;
    }
    const handleIntroComplete = () => setVisible(true);
    window.addEventListener('introComplete', handleIntroComplete);
    return () =>
      window.removeEventListener('introComplete', handleIntroComplete);
  }, [location.pathname]);

  const saveConsent = (consent: Consent) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    setVisible(false);
  };

  const acceptAll = () =>
    saveConsent({ functional: true, analytics: true, marketing: true });

  const essentialOnly = () =>
    saveConsent({ functional: false, analytics: false, marketing: false });

  const savePreferences = () => saveConsent(prefs);

  if (!visible) return null;

  return (
    <div className="cookie-banner">
      <div className="cookie-banner-inner">
        <p className="cookie-banner-text">
          {t.text}{' '}
          <Link to="/privacy" className="cookie-banner-link">
            {t.policyLink}
          </Link>
        </p>

        {showSettings && (
          <div className="cookie-banner-settings">
            <label className="cookie-banner-toggle">
              <input type="checkbox" checked readOnly disabled />
              {t.essentialLabel}
            </label>
            <label className="cookie-banner-toggle">
              <input
                type="checkbox"
                checked={prefs.functional}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setPrefs((p: Consent) => ({ ...p, functional: e.target.checked }))
                }
              />
              {t.functionalLabel}
            </label>
            <label className="cookie-banner-toggle">
              <input
                type="checkbox"
                checked={prefs.analytics}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setPrefs((p: Consent) => ({ ...p, analytics: e.target.checked }))
                }
              />
              {t.analyticsLabel}
            </label>
            <label className="cookie-banner-toggle">
              <input
                type="checkbox"
                checked={prefs.marketing}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setPrefs((p: Consent) => ({ ...p, marketing: e.target.checked }))
                }
              />
              {t.marketingLabel}
            </label>
          </div>
        )}

        <div className="cookie-banner-actions">
          <button
            type="button"
            className="cookie-banner-btn cookie-banner-btn-primary"
            onClick={acceptAll}
          >
            {t.acceptAll}
          </button>
          {showSettings ? (
            <button
              type="button"
              className="cookie-banner-btn"
              onClick={savePreferences}
            >
              {t.savePreferences}
            </button>
          ) : (
            <button
              type="button"
              className="cookie-banner-btn"
              onClick={() => setShowSettings(true)}
            >
              {t.settings}
            </button>
          )}
          <button
            type="button"
            className="cookie-banner-btn"
            onClick={essentialOnly}
          >
            {t.essentialOnly}
          </button>
        </div>
      </div>
    </div>
  );
}